import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type {
  YouTubeNewVideoPrivacy,
  YouTubeNewVideoTarget
} from '@utils/types/publishing/publish.ts';

interface Props {
  draft: YouTubeNewVideoTarget;
  onChange: (next: YouTubeNewVideoTarget) => void;
  /**
   * Backend has passed the YouTube API services audit for this
   * deployment — only then does Google honour ``public``. False
   * means we hide the public radio (the connector would clamp it
   * to private anyway, so offering it would be a lie).
   */
  publicAllowed: boolean;
}

const PRIVACY_CHOICES: readonly YouTubeNewVideoPrivacy[] = [
  'private',
  'unlisted',
  'public'
] as const;

/**
 * Form fields the user fills in when uploading as a new YouTube video.
 * Lives in its own file so the existing-video picker stays readable;
 * both branches are composed by {@link StepChooseTarget}.
 *
 * Privacy picker (ticket #4101 follow-up Section 2): user chooses
 * private, unlisted, or public. The backend clamps public → private
 * when the YouTube audit hasn't cleared, so the UI hides the public
 * radio in that case instead of silently downgrading post-submit.
 */
export default function StepChooseTargetNewVideo({
  draft,
  onChange,
  publicAllowed
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const requested: YouTubeNewVideoPrivacy = draft.privacy ?? 'private';
  // Hiding the public radio is not enough on its own: a draft that already
  // says `public` would keep saying it while no radio appeared selected,
  // and that value would go up with the payload. Clamp it, and push the
  // correction back so the draft itself cannot carry a choice this
  // connection does not allow.
  const privacy: YouTubeNewVideoPrivacy =
    !publicAllowed && requested === 'public' ? 'private' : requested;

  // Deliberately keyed on the clamp alone: `draft` changes on every
  // keystroke and `onChange` is a fresh closure each render, so widening
  // the deps would fire this on every edit.
  useEffect(() => {
    if (privacy !== draft.privacy) onChange({ ...draft, privacy });
  }, [privacy, draft.privacy]);

  const visibleChoices = PRIVACY_CHOICES.filter(
    choice => choice !== 'public' || publicAllowed
  );

  const privacyLabel = (choice: YouTubeNewVideoPrivacy) => {
    switch (choice) {
      case 'private':
        return t('publish-new-video-privacy-private');
      case 'unlisted':
        return t('publish-new-video-privacy-unlisted');
      case 'public':
        return t('publish-new-video-privacy-public');
    }
  };

  const privacyHelp = (choice: YouTubeNewVideoPrivacy) => {
    switch (choice) {
      case 'private':
        return t('publish-new-video-privacy-private-help');
      case 'unlisted':
        return t('publish-new-video-privacy-unlisted-help');
      case 'public':
        return t('publish-new-video-privacy-public-help');
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium">
          {t('publish-new-video-title-label')}
        </span>
        <input
          type="text"
          required
          maxLength={100}
          value={draft.title}
          onChange={e => onChange({ ...draft, title: e.target.value })}
          placeholder={t('publish-new-video-title-placeholder')}
          aria-label={t('publish-new-video-title-label')}
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
          style={{
            borderColor: getColour('inputBorder'),
            background: getColour('firstLayer'),
            color: getColour('normalText')
          }}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium">
          {t('publish-new-video-description-label')}
        </span>
        <textarea
          rows={3}
          maxLength={5000}
          value={draft.description ?? ''}
          onChange={e => onChange({ ...draft, description: e.target.value })}
          placeholder={t('publish-new-video-description-placeholder')}
          aria-label={t('publish-new-video-description-label')}
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 resize-none"
          style={{
            borderColor: getColour('inputBorder'),
            background: getColour('firstLayer'),
            color: getColour('normalText')
          }}
        />
      </label>

      <fieldset
        className="flex flex-col gap-2 rounded-lg border px-3 py-2"
        style={{
          borderColor: getColour('hairline'),
          background: getColour('firstLayer')
        }}
      >
        <legend
          className="px-1 text-xs font-medium"
          style={{ color: getColour('normalText') }}
        >
          {t('publish-new-video-privacy-legend')}
        </legend>
        {visibleChoices.map(choice => (
          <label
            key={choice}
            className="flex cursor-pointer items-start gap-2 text-xs"
          >
            <input
              type="radio"
              name="publish-new-video-privacy"
              value={choice}
              checked={privacy === choice}
              onChange={() => onChange({ ...draft, privacy: choice })}
              className="mt-0.5"
            />
            <span className="flex flex-col">
              <span
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {privacyLabel(choice)}
              </span>
              <span style={{ color: getColour('inactiveText') }}>
                {privacyHelp(choice)}
              </span>
            </span>
          </label>
        ))}
        {!publicAllowed && (
          <p
            className="pl-6 text-xs italic"
            style={{ color: getColour('inactiveText') }}
          >
            {t('publish-new-video-privacy-public-unavailable')}
          </p>
        )}
      </fieldset>
    </div>
  );
}
